import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Skeleton } from "@mui/material";

const SAFE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
]);

interface LocalFileImagePreviewProps {
  file: File;
}

/**
 * Image preview for a local `File` only. The object URL is created and revoked here so `img` `src`
 * is never fed from generic string state (satisfies static analysis for blob previews).
 */
const LocalFileImagePreview: React.FC<LocalFileImagePreviewProps> = ({
  file,
}) => {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // React 18 StrictMode double-invokes effects in development, which can cause premature revocation
  // of blob URLs and net::ERR_FILE_NOT_FOUND. This does not affect production.
  useEffect(() => {
    if (!SAFE_IMAGE_TYPES.has(file.type)) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors if the URL was already revoked or not found
      }
    };
  }, [file]);

  if (!objectUrl) {
    return (
      <Skeleton
        variant="rounded"
        aria-busy="true"
        aria-label={t("Loading preview")}
        sx={{ width: "100%", height: "100%", borderRadius: "var(--radius-lg)" }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={objectUrl}
      alt=""
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        borderRadius: "var(--radius-lg)",
      }}
    />
  );
};

export default LocalFileImagePreview;
